/**
 * The HTTP client. The only way this app reaches data.
 *
 * There is no database import anywhere in `apps/web`, and
 * `packages/contract/src/boundary.test.ts` fails the build if one appears. That
 * is the server-only boundary in practice: `packages/db` holds the Postgres URL
 * and `packages/integrations` holds vendor bearer tokens, so the browser gets
 * neither — it gets JSON from a backend that owns both.
 */
import { CSRF_COOKIE, CSRF_HEADER, PERSONA_HEADER, isSiteMaintenanceStatus, type ApiError, type RequiredFieldState, type SiteMaintenanceStatus, type TransitionResult, type VendorCandidate } from "@truckbuddy/contract";

/**
 * Fired on `window` when any request is refused by an active maintenance window,
 * with the window as `detail`. `useSiteMaintenanceSync` listens and switches the
 * shell to the maintenance page on that failed call, rather than on the next
 * poll -- a save that fails with a bare 503 and only later explains itself
 * reads as a bug.
 */
export const SITE_MAINTENANCE_EVENT = "truckbuddy:site-maintenance";

/**
 * A failure with the server's structured body preserved.
 *
 * The two fields past `message` are the reason this class exists rather than a
 * bare `Error`:
 *
 * - `missingRequiredFields` (from a 422) lets the caller open a completion panel
 *   prefilled to what is missing, instead of showing a toast the user cannot act
 *   on.
 * - `current` (from a 409) carries the authoritative stage after a concurrent
 *   move, so the screen re-renders the truth instead of retrying blind.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ApiError["error"]["code"] | "network";
  readonly sqlState: string | undefined;
  readonly missingRequiredFields: RequiredFieldState[] | undefined;
  readonly current: TransitionResult | undefined;
  readonly candidates: VendorCandidate[] | undefined;
  /** Set when the caller may override the refusal by confirming and sending again. */
  readonly acknowledge: ApiError["error"]["acknowledge"];
  /** On a 503 `site_maintenance`: the window that refused the request. */
  readonly maintenance: SiteMaintenanceStatus | undefined;

  constructor(
    status: number,
    body: ApiError["error"] | { code: "network"; message: string },
  ) {
    super(body.message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = body.code;
    this.sqlState = "sqlState" in body ? body.sqlState : undefined;
    this.missingRequiredFields =
      "missingRequiredFields" in body ? body.missingRequiredFields : undefined;
    this.current = "current" in body ? body.current : undefined;
    this.candidates = "candidates" in body ? body.candidates : undefined;
    this.acknowledge = "acknowledge" in body ? body.acknowledge : undefined;
    this.maintenance =
      "maintenance" in body && isSiteMaintenanceStatus(body.maintenance) ? body.maintenance : undefined;
  }

  /** Refused because TruckBuddy is down for maintenance. */
  get isSiteMaintenance(): boolean {
    return this.status === 503 && this.code === "site_maintenance";
  }

  /** A 409 the caller can resolve by re-rendering rather than by asking the user. */
  get isStaleStage(): boolean {
    return this.status === 409 && this.current !== undefined;
  }

  /** A 422 whose remedy is filling in named fields. */
  get isIncompleteStage(): boolean {
    return this.status === 422 && (this.missingRequiredFields?.length ?? 0) > 0;
  }
}

/**
 * The persona the dev switcher selected, mirrored into `localStorage` so a
 * reload does not silently change who you are.
 *
 * This is **development-only** and the API is the gate, not this: with
 * `API_DEV_PERSONAS` off, the header is ignored entirely and the request is
 * rejected. B4 — the switcher must not ship — is enforced there, not here.
 */
const PERSONA_STORAGE_KEY = "truckbuddy.persona";

export function readStoredPersona(): string | null {
  try {
    return window.localStorage.getItem(PERSONA_STORAGE_KEY);
  } catch {
    // Private browsing, or storage disabled. Falling back to the API's default
    // persona is correct — it just means the choice does not survive a reload.
    return null;
  }
}

export function writeStoredPersona(persona: string | null): void {
  try {
    if (persona === null) window.localStorage.removeItem(PERSONA_STORAGE_KEY);
    else window.localStorage.setItem(PERSONA_STORAGE_KEY, persona);
  } catch {
    /* see above */
  }
}

const BASE = "/api";

interface RequestOptions {
  // PUT is here for the one endpoint that replaces a whole set rather than
  // amending one — `PUT /work-orders/:id/categories`. The verb is the contract:
  // the body is the intended final set, so a repeat of the same request is a
  // no-op by construction rather than by idempotency key.
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Query parameters. Undefined, null and empty-string values are dropped, so a
   * cleared filter removes the parameter rather than sending `?q=`.
   *
   * Typed as `object` rather than `Record<string, …>` deliberately: the callers
   * pass plain interfaces (`QueueParams`, `AssetParams`, …), and a `Record`
   * parameter rejects an interface without an index signature. Widening here is
   * better than sprinkling index signatures across five interfaces that exist to
   * document a specific set of filters — the value coercion below is what makes
   * it safe.
   */
  query?: object;
}

function buildUrl(path: string, query: RequestOptions["query"]): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    // Only scalars become query parameters. An object or array would stringify to
    // "[object Object]" and produce a request that silently ignores the filter.
    if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
      continue;
    }
    url.searchParams.set(key, String(value));
  }
  return `${url.pathname}${url.search}`;
}

/**
 * The shared transport: persona header, and the one place a dead network becomes
 * an `ApiClientError` rather than a raw `TypeError`.
 *
 * Split out of `apiRequest` so `apiText` can reuse it. Before the split the CSV
 * export carried its own copy of this and of `failFor` below, which is how two
 * error paths drift apart — one of them learns about a new status code and the
 * other does not.
 */
/**
 * The CSRF token, read back out of the cookie the API set at sign-in.
 *
 * `tb_csrf` is deliberately NOT httpOnly — that is the whole mechanism of a
 * double-submit: the page can read this one, and the `tb_session` cookie beside
 * it stays unreadable. Sending it back in a header is what proves the request
 * came from a page on this origin, because a cross-site attacker can cause the
 * cookies to be SENT but cannot read them to build the header.
 *
 * The client half did not exist until UAT 0908 finding 14 — the server issued
 * the token and compared it nowhere, and this file sent no such header, so
 * neither end of the control ran.
 *
 * Returns null rather than throwing when the cookie is absent: a signed-out
 * page has no token, and its requests are refused for want of a session long
 * before the header matters.
 */
function readCsrfToken(): string | null {
  for (const part of document.cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === CSRF_COOKIE) return decodeURIComponent(rest.join("="));
  }
  return null;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const persona = readStoredPersona();
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["content-type"] = "application/json";
  if (persona) headers[PERSONA_HEADER] = persona;
  // Every method, not just the writing ones: the API exempts GET/HEAD/OPTIONS,
  // and sending it anyway costs nothing and means one rule here instead of two.
  const csrf = readCsrfToken();
  if (csrf) headers[CSRF_HEADER] = csrf;

  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal ?? null,
    });
  } catch {
    // An offline browser and a dead API are the same shape here. Distinguishing
    // them needs a server, which is the thing that is missing.
    throw new ApiClientError(0, {
      code: "network",
      message: "Could not reach the server. Check that the API is running.",
    });
  }
}

/**
 * Turn a non-OK response into `ApiClientError`, keeping the server's structured
 * body when there is one so a 409's `current` and a 422's `missingRequiredFields`
 * survive. Falls back to the raw text, truncated, when the body is not ours.
 */
async function failFor(response: Response): Promise<never> {
  const text = await response.text();
  const parsed: unknown = text ? safeJsonParse(text) : null;
  const body =
    parsed && typeof parsed === "object" && "error" in parsed
      ? (parsed as ApiError).error
      : { code: "internal" as const, message: text.slice(0, 300) || response.statusText };
  const error = new ApiClientError(response.status, body);
  if (error.isSiteMaintenance && error.maintenance && typeof window !== "undefined") {
    try {
      // `window.CustomEvent`, not the global: the two are the same object in a
      // browser, but a test DOM rejects an event built in another realm.
      window.dispatchEvent(new window.CustomEvent(SITE_MAINTENANCE_EVENT, { detail: error.maintenance }));
    } catch {
      // Announcing the window is a courtesy. It must never replace the error the
      // caller is about to handle, and the poll catches up within seconds.
    }
  }
  throw error;
}

/**
 * POST to an /auth/* route.
 *
 * These sit OUTSIDE the /api prefix, because the scope behind /api requires a
 * session and nobody holds one before signing in. apiRequest prefixes every
 * path with /api, so signing in cannot go through it.
 *
 * No credentials option: the default for a same-origin fetch already sends and
 * accepts cookies, and same-origin is exactly what serving the SPA from the API
 * container buys. The session cookie is SameSite=Lax and would not travel
 * cross-site anyway.
 */
export async function authRequest<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiClientError(0, {
      code: "network",
      message: "Could not reach the server. Check that the API is running.",
    });
  }
  if (response.status === 204) return undefined as T;
  if (!response.ok) await failFor(response);
  const text = await response.text();
  return (text ? safeJsonParse(text) : null) as T;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204) return undefined as T;
  if (!response.ok) await failFor(response);

  const text = await response.text();
  return (text ? safeJsonParse(text) : null) as T;
}

/**
 * Upload one file to a document route.
 *
 * The first multipart caller in the app, and it cannot go through `apiRequest`
 * for two reasons that are both about `send()`:
 *
 * 1. `send()` sets `content-type: application/json` whenever a body is present
 *    and runs it through `JSON.stringify`. A `FormData` would arrive as
 *    `"{}"`.
 * 2. `RequestOptions` carries no `headers`, and these routes require an
 *    idempotency header of at least 8 characters
 *    (`apps/api/src/routes/documents.ts`).
 *
 * **Content-Type is deliberately not set.** `fetch` writes it itself for a
 * `FormData` body, including the multipart boundary — which is generated per
 * request and which we cannot know. Setting the header by hand produces a
 * boundary-less content type and the server fails to parse a body that is
 * perfectly well formed.
 *
 * The idempotency key is the caller's, not this function's: a retry after a
 * dropped connection must carry the SAME key, so generating one here would make
 * every retry a new document. `newIdempotencyKey` is the intended source.
 */
export async function apiUpload<T>(
  path: string,
  file: File,
  options: {
    idempotencyKey: string;
    docClass?: string;
    relationship?: string;
    continuation?: boolean;
    continuationInvoiceNumber?: string;
    /**
     * An asset paper's dates (TRBUD-355), sent as multipart fields the server's
     * `spool()` reads. Blank is omitted. Before TRBUD-342 these were accepted here
     * and never appended, so every date typed at upload was silently dropped and a
     * paper type that requires an expiry refused every upload.
     */
    effectiveFrom?: string;
    expiresAt?: string;
    /**
     * Any other multipart fields, sent before the file (TRBUD-466: a price
     * book's Replace sends its `reason` this way). Blank values are omitted.
     */
    fields?: Readonly<Record<string, string>>;
  } = {
    idempotencyKey: "",
  },
): Promise<T> {
  const form = new FormData();
  for (const [name, value] of Object.entries(options.fields ?? {})) {
    if (value.trim()) form.append(name, value.trim());
  }
  // `docClass` and `relationship` are read off the multipart FIELDS by the
  // server's `spool()`, and both are optional there. Appending an empty value
  // is not the same as omitting it — the server trims and treats blank as
  // absent, but sending nothing keeps the intent legible on the wire.
  if (options.docClass) form.append("docClass", options.docClass);
  if (options.relationship) form.append("relationship", options.relationship);
  // TRBUD-352: "This file continues another receipt", and the invoice number
  // printed on that page. Before the file part, like the class: the server
  // reads only the fields that precede it.
  if (options.continuation) form.append("continuation", "true");
  if (options.continuation && options.continuationInvoiceNumber?.trim()) {
    form.append("continuationInvoiceNumber", options.continuationInvoiceNumber.trim());
  }
  if (options.effectiveFrom?.trim()) form.append("effectiveFrom", options.effectiveFrom.trim());
  if (options.expiresAt?.trim()) form.append("expiresAt", options.expiresAt.trim());
  form.append("file", file, file.name);

  const persona = readStoredPersona();
  const headers: Record<string, string> = {
    "x-truckbuddy-idempotency-key": options.idempotencyKey,
  };
  if (persona) headers[PERSONA_HEADER] = persona;
  // The upload is a POST under /api, so it needs the token like every other
  // write. `content-type` is still left to the browser, which must set the
  // multipart boundary itself.
  const csrf = readCsrfToken();
  if (csrf) headers[CSRF_HEADER] = csrf;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, undefined), {
      method: "POST",
      headers,
      body: form,
    });
  } catch {
    throw new ApiClientError(0, {
      code: "network",
      message: "Could not reach the server. Check that the API is running.",
    });
  }

  if (!response.ok) await failFor(response);
  const text = await response.text();
  return (text ? safeJsonParse(text) : null) as T;
}

/**
 * A response whose body is **not** JSON — today, the CSV export.
 *
 * `apiRequest` runs every body through `JSON.parse`, so a CSV comes back `null`
 * and the download is an empty file. This returns the bytes unmodified, and the
 * `headers` alongside them because for a file the body IS the payload: a caller
 * handed 1,000 rows has to tell "that is all of it" from "that is where we
 * stopped" without parsing the CSV back apart.
 *
 * Deliberately no BOM and no re-encoding. A disputed export has to be byte-identical
 * to what `report_export` recorded, or the audit trail describes a different file
 * from the one the argument is about.
 */
export async function apiText(
  path: string,
  options: RequestOptions = {},
): Promise<{ text: string; headers: Headers }> {
  const response = await send(path, options);
  if (!response.ok) await failFor(response);
  return { text: await response.text(), headers: response.headers };
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * A fresh idempotency key per user intent.
 *
 * Generated in the **client**, not the server, and generated once when the user
 * commits — not once per retry. That is what makes it useful: a retried submit
 * after a dropped connection carries the same key and cannot create a second
 * row. An offline driver draft depends on exactly this.
 */
export function newIdempotencyKey(prefix: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`;
}
